<!-- src/components/GoogleReviewsCard.vue -->
<template>
  <div class="gr-card card">
    <details class="gr-collapsible">
      <summary class="gr-header">
        <div class="gr-heading">
          <span class="gr-heading-icon" aria-hidden="true">★</span>
          <div>
            <div class="gr-title">Google Reviews</div>
            <div class="gr-subtitle">Current Google rating and customer feedback</div>
          </div>
        </div>

        <div v-if="loading && !data" class="gr-header-message">
          Loading Google reviews…
        </div>
        <div v-else-if="!venueId" class="gr-header-message">No venue selected</div>
        <div v-else-if="error && !data" class="gr-header-message gr-error-text">
          Reviews unavailable
        </div>
        <div v-else-if="data" class="gr-metrics">
          <div class="gr-metric">
            <span class="gr-metric-label">Google rating</span>
            <strong class="gr-metric-value">
              {{ displayRating(data.averageRating) }}
              <small>/ 5</small>
            </strong>
          </div>

          <div class="gr-metric">
            <span class="gr-metric-label">Total reviews</span>
            <strong class="gr-metric-value">
              {{ formatNumber(data.totalReviewCount) }}
            </strong>
          </div>

          <div class="gr-metric">
            <span class="gr-metric-label"> Awaiting reply (latest 15) </span>
            <strong class="gr-metric-value">
              {{ awaitingReplies }}
            </strong>
          </div>

          <div class="gr-metric gr-status-metric">
            <span class="gr-metric-label">Current status</span>
            <span
              class="gr-status-pill"
              :style="{
                backgroundColor: ragBackground(overallRag) || '#e5e7eb',
              }"
            >
              {{ ragLabel(overallRag) }}
            </span>
          </div>

          <div class="gr-metric gr-updated-metric">
            <span class="gr-metric-label">Last updated</span>
            <strong class="gr-updated-value">
              {{ formatDateTime(data.refreshedAt) }}
            </strong>
          </div>
        </div>

        <span class="gr-chevron" aria-hidden="true"></span>
      </summary>

      <div class="gr-content">
        <div v-if="!venueId" class="gr-empty">
          Select a venue to display its Google reviews.
        </div>

        <div v-else-if="loading && !data" class="gr-empty">
          <div class="progress">
            <div class="indeterminate"></div>
          </div>
          Retrieving Google reviews…
        </div>

        <div v-else-if="error && !data" class="gr-empty gr-error-text">
          <p>{{ error }}</p>
          <button class="btn-small" type="button" @click="loadReviews">Try again</button>
        </div>

        <template v-else-if="data">
          <div class="gr-content-top">
            <div>
              <strong>Latest Google Reviews</strong>
              <div class="gr-content-note">
                Showing {{ reviews.length }} of up to 15 reviews, newest update first
              </div>
            </div>

            <a
              v-if="data.googleMapsUrl"
              :href="data.googleMapsUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="gr-link"
            >
              View on Google ↗
            </a>
          </div>

          <div v-if="data.stale" class="gr-stale">
            Google's latest data is temporarily unavailable; showing the last successful
            daily update.
          </div>

          <div v-if="reviews.length === 0" class="gr-empty">
            No reviews are available for this venue.
          </div>

          <div v-else class="gr-review-list">
            <article
              v-for="(review, index) in reviews"
              :key="review.id || index"
              class="gr-review-row"
              :class="`gr-rag-${reviewRag(review.stars)}`"
              :style="{
                backgroundColor: ragBackground(reviewRag(review.stars)) || '#f1f5f9',
              }"
            >
              <div class="gr-review-top">
                <div class="gr-review-person">
                  <strong>
                    {{ review.reviewer || "Google user" }}
                  </strong>
                  <span class="gr-review-date">
                    {{ formatDate(review.createdAt) }}
                  </span>
                </div>

                <div class="gr-review-rating">
                  <span class="gr-review-stars" aria-hidden="true">
                    {{ starDisplay(review.stars) }}
                  </span>
                  <strong> {{ displayRating(review.stars) }} / 5 </strong>
                  <span class="gr-row-rag">
                    {{ ragLabel(reviewRag(review.stars)) }}
                  </span>
                </div>
              </div>

              <p v-if="review.comment" class="gr-review-comment">
                {{ review.comment }}
              </p>
              <p v-else class="gr-review-no-comment">
                Star rating only — no written comment.
              </p>

              <div v-if="review.reply" class="gr-reply">
                <strong>Management reply</strong>
                <p>{{ review.reply }}</p>
              </div>
              <div v-else class="gr-no-reply">No management reply</div>
            </article>
          </div>

          <div class="gr-footer">
            Google data is refreshed a maximum of once every 24 hours per venue.
            <span v-if="data.stale">Cached information</span>
          </div>
        </template>
      </div>
    </details>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from "vue";

import { getFunctions, httpsCallable } from "firebase/functions";

import {
  RAG,
  ragHigherIsBetter,
  ragBackground,
  ragLabel,
} from "@/scripts/sharedHelpers/auditColours";

const props = defineProps({
  venueId: {
    type: String,
    default: "",
  },
});

const data = ref(null);
const loading = ref(false);
const error = ref("");

const reviews = computed(() =>
  Array.isArray(data.value?.reviews) ? data.value.reviews.slice(0, 15) : []
);

const awaitingReplies = computed(
  () => reviews.value.filter((review) => !String(review.reply || "").trim()).length
);

// Same Firebase Functions region as VenueDashboard.vue
const getReviews = httpsCallable(
  getFunctions(undefined, "us-central1"),
  "getVenueGoogleReviews"
);

/*
  Google Reviews RAG thresholds:

  1–2 stars       RED
  2.5–3.5 stars   AMBER
  4–5 stars       GREEN

  Uses the existing CoreDeck RAG functions.
*/
function reviewRag(rating) {
  if (rating === null || rating === undefined || rating === "") {
    return RAG.GREY;
  }

  const value = Number(rating);

  if (!Number.isFinite(value) || value < 1 || value > 5) {
    return RAG.GREY;
  }

  return ragHigherIsBetter(value, {
    greenMin: 4,
    amberMin: 2.5,
  });
}

const overallRag = computed(() => reviewRag(data.value?.averageRating));

function displayRating(value) {
  if (
    value === null ||
    value === undefined ||
    value === "" ||
    !Number.isFinite(Number(value))
  ) {
    return "—";
  }

  return Number(value).toFixed(1);
}

function starDisplay(value) {
  const count = Math.max(0, Math.min(5, Math.round(Number(value) || 0)));

  return "★".repeat(count) + "☆".repeat(5 - count);
}

function formatNumber(value) {
  return new Intl.NumberFormat("en-GB").format(Number(value) || 0);
}

function formatDate(value) {
  if (!value) return "Date unavailable";

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return "Date unavailable";
  }

  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "Europe/London",
  }).format(parsed);
}

function formatDateTime(value) {
  if (!value) return "Not updated";

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return "Not updated";
  }

  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/London",
  }).format(parsed);
}

let requestVersion = 0;

async function loadReviews() {
  const selectedVenue = String(props.venueId || "").trim();

  const request = ++requestVersion;

  error.value = "";
  data.value = null;

  if (!selectedVenue) {
    loading.value = false;
    return;
  }

  loading.value = true;

  try {
    const response = await getReviews({
      venueId: selectedVenue,
    });

    if (request !== requestVersion) return;

    data.value = response.data;
  } catch (err) {
    if (request !== requestVersion) return;

    error.value = err?.message || "Unable to retrieve Google reviews.";
  } finally {
    if (request === requestVersion) {
      loading.value = false;
    }
  }
}

watch(() => props.venueId, loadReviews, { immediate: true });

onBeforeUnmount(() => {
  requestVersion++;
});
</script>

<style scoped>
.gr-card {
  margin: 0;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 7px rgba(0, 0, 0, 0.18);
}

.gr-collapsible {
  background: #fff;
}

.gr-header {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 20px 24px;
  min-height: 105px;
  cursor: pointer;
  list-style: none;
}

.gr-header::-webkit-details-marker {
  display: none;
}

.gr-header::marker {
  content: "";
}

.gr-header:focus-visible {
  outline: 3px solid #1976d2;
  outline-offset: -3px;
}

.gr-heading {
  display: flex;
  align-items: center;
  gap: 14px;
  flex: 0 0 220px;
}

.gr-heading-icon {
  font-size: 29px;
  color: #e9aa18;
  line-height: 1;
}

.gr-title {
  font-size: 21px;
  font-weight: 700;
  color: #182434;
}

.gr-subtitle {
  margin-top: 4px;
  font-size: 12px;
  color: #64748b;
}

.gr-metrics {
  flex: 1;
  display: grid;
  grid-template-columns: repeat(5, minmax(85px, 1fr));
  gap: 14px;
  align-items: center;
}

.gr-metric {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.gr-metric-label {
  color: #64748b;
  font-size: 12px;
  line-height: 1.25;
}

.gr-metric-value {
  font-size: 23px;
  font-weight: 700;
  color: #172334;
  line-height: 1.2;
  white-space: nowrap;
}

.gr-metric-value small {
  font-size: 12px;
  color: #64748b;
}

.gr-status-pill {
  align-self: flex-start;
  border-radius: 5px;
  padding: 5px 9px;
  font-size: 12px;
  font-weight: 700;
  color: #243041;
}

.gr-updated-value {
  color: #172334;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.4;
}

.gr-header-message {
  flex: 1;
  color: #64748b;
  font-size: 14px;
}

.gr-chevron {
  border: solid #5b6878;
  border-width: 0 2px 2px 0;
  padding: 4px;
  transform: rotate(45deg);
  transition: transform 0.18s;
  flex: 0 0 auto;
  margin-right: 5px;
}

.gr-collapsible[open] .gr-chevron {
  transform: rotate(225deg);
}

.gr-content {
  padding: 20px 24px 22px;
  border-top: 1px solid #e2e8f0;
}

.gr-content-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 14px;
  color: #172334;
}

.gr-content-note {
  color: #64748b;
  font-size: 12px;
  margin-top: 4px;
}

.gr-link {
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
}

.gr-review-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 650px;
  overflow-y: auto;
  padding-right: 4px;
}

.gr-review-row {
  border-left: 6px solid #cbd5e1;
  border-radius: 6px;
  padding: 13px 16px;
  color: #172334;
}

.gr-review-row.gr-rag-green {
  border-left-color: #2e7d32;
}

.gr-review-row.gr-rag-amber {
  border-left-color: #ef9100;
}

.gr-review-row.gr-rag-red {
  border-left-color: #c62828;
}

.gr-review-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.gr-review-person {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.gr-review-person strong {
  font-size: 14px;
}

.gr-review-date {
  font-size: 12px;
  color: #526071;
}

.gr-review-rating {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 12px;
}

.gr-review-stars {
  letter-spacing: 1px;
  color: #ad6900;
  font-size: 17px;
}

.gr-row-rag {
  font-weight: 700;
  font-size: 11px;
}

.gr-review-comment,
.gr-reply p {
  white-space: pre-line;
  overflow-wrap: anywhere;
  line-height: 1.5;
  font-size: 14px;
}

.gr-review-comment {
  margin: 10px 0 8px;
}

.gr-review-no-comment,
.gr-no-reply {
  font-size: 12px;
  color: #596577;
  margin: 10px 0 0;
}

.gr-reply {
  border-top: 1px solid rgba(25, 35, 48, 0.14);
  margin-top: 10px;
  padding-top: 10px;
}

.gr-reply strong {
  font-size: 12px;
}

.gr-reply p {
  margin: 5px 0 0;
}

.gr-empty {
  padding: 20px 0;
  text-align: center;
  color: #64748b;
}

.gr-error-text {
  color: #b71c1c;
}

.gr-stale {
  margin-bottom: 12px;
  padding: 10px 12px;
  border-radius: 5px;
  background: #fff3e0;
  color: #8d4c00;
  font-size: 13px;
}

.gr-footer {
  margin-top: 14px;
  color: #64748b;
  font-size: 12px;
  display: flex;
  justify-content: space-between;
}

@media (max-width: 1100px) {
  .gr-header {
    flex-wrap: wrap;
  }

  .gr-heading {
    flex-basis: calc(100% - 45px);
  }

  .gr-metrics {
    order: 3;
    flex-basis: 100%;
  }

  .gr-chevron {
    margin-left: auto;
  }
}

@media (max-width: 650px) {
  .gr-header {
    padding: 16px;
    gap: 14px;
  }

  .gr-content {
    padding: 16px;
  }

  .gr-title {
    font-size: 18px;
  }

  .gr-metrics {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .gr-updated-metric {
    grid-column: span 2;
  }

  .gr-review-rating {
    flex-wrap: wrap;
    gap: 5px 9px;
  }

  .gr-content-top {
    flex-wrap: wrap;
  }
}
</style>
